/* istanbul ignore file */ /* this file is not used or will be deleted in other branches */
// @ts-nocheck
import React, { KeyboardEvent, useCallback, useEffect, useState } from 'react';
import { TextField } from '@td-client-frontend/common';
import clsx from 'clsx';
import GridRowFlex from './GridRowFlex';
import { useDdf } from '../../context/ddf-context';
import Sorting from '../Sorting';
import { MagnifyingGlass as SearchIcon, CloseIcon as Clear } from '@td-client-frontend/icons';

enum IconStates {
  blur,
  focus
}

const GridFlex = ({
  fieldArrayRenderProps,
  gridFields,
  searchPlaceholder,
  flattenFields,
  setEdit,
  fieldKey,
  ...rest
}) => {
  const { handlers } = useDdf();
  const [isChecked, setIsChecked] = useState(false);
  const [fractionGrid, setFractionGrid] = useState('');
  const [hasCheckCol, setHasCheckCol] = useState(false);
  const [isTableEmpty, setIsTableEmpty] = useState(false);
  const [searchString, setSearchString] = useState('');
  const [searchFieldIcon, setSearchFieldIcon] = useState<IconStates>(IconStates.blur);

  const calculateFractions = useCallback(() => {
    return (
      flattenFields
        ?.filter((column) => !!column.fractionSize)
        .map(({ fractionSize }) => fractionSize)
        .reverse()
        .reduce((newFrac, accFrac = '') => accFrac + ' ' + newFrac)
        // Manage button fraction
        .concat(' 1fr 1fr')
    );
  }, [flattenFields]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const hasCheckboxField = gridFields?.some(({ name }) => name === 'checked');
    setHasCheckCol(hasCheckboxField);
    setFractionGrid(calculateFractions());
  });

  useEffect(() => {
    setIsTableEmpty(!fieldArrayRenderProps.meta.initial.length);
  }, [fieldArrayRenderProps.meta.initial.length]);

  const checkBoxHandler = () => {
    setIsChecked((prevState) => !prevState);
  };

  const onKeyUp = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') handlers.search(searchString.toLowerCase(), true);
  };

  const handleSubmit = () => {
    handlers.search(searchString.toLowerCase());
  };

  const handleClear = () => {
    setSearchString('');
    setSearchFieldIcon(IconStates.blur);
    handlers.search();
  };

  const icons = {
    [IconStates.blur]: (
      <>
        <button
          id={IconStates.blur.toString()}
          className="flex items-center px-4 self-stretch h-full focus:outline-none focus-visible:ring disabled:opacity-50 bg-primary-600"
          aria-label="Search"
          type="button"
          onClick={handleSubmit}
        >
          <SearchIcon className="fill-white" aria-hidden />
        </button>
      </>
    ),
    [IconStates.focus]: (
      <>
        <button
          id={IconStates.focus.toString()}
          className="flex items-center self-stretch h-full px-4 focus:outline-none focus-visible:ring disabled:opacity-50"
          aria-label="Clear"
          type="button"
          onClick={handleClear}
        >
          <Clear className="fill-black w-4" aria-hidden />
        </button>
      </>
    )
  };

  const handleChange = (e) => {
    const value = e.target.value;

    if (value) setSearchFieldIcon(IconStates.focus);
    if (!value) setSearchFieldIcon(IconStates.blur);

    setSearchString(value);
    handlers.search(value.toLowerCase());
  };

  const onSearchFocus = (e) => {
    if (e.target.value) setSearchFieldIcon(IconStates.focus);
  };

  const onSearchBlur = (e) => {
    const value = e.target.value;

    if (!value) setSearchFieldIcon(IconStates.blur);

    handlers.search(value.toLowerCase());
  };

  return (
    <>
      <div className="flex">
        <div className="w-1/4">
          <TextField
            name="search"
            onChange={handleChange}
            onFocus={onSearchFocus}
            onBlur={onSearchBlur}
            value={searchString}
            placeholder={searchPlaceholder}
            onKeyUp={onKeyUp}
            Right={icons[searchFieldIcon]}
            aria-label="Search Field"
            label={isTableEmpty ? 'No results found. Try adjusting your search criteria' : null}
          />
        </div>
      </div>
      <div className="w-full">
        <div
          className="px-2 py-2 uppercase border-b-2 border-primary-500 text-gray-30"
          style={{
            display: 'grid',
            gridTemplateColumns: fractionGrid
          }}
        >
          {hasCheckCol ? (
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center flex-shrink-0 w-5 h-5 mr-2 rounded field-with-icons">
                <input type="checkbox" className="absolute opacity-0" checked={isChecked} onChange={checkBoxHandler} />
                <svg
                  viewBox="0 0 20 20"
                  className={clsx('fill-current w-3 h-3 text-primary-500 pointer-events-none', !isChecked && 'hidden')}
                >
                  <path d="M0 11l2-2 5 5L18 3l2 2L7 18z" />
                </svg>
              </div>
            </div>
          ) : null}
          {gridFields
            ?.filter((fieldItem) => fieldItem.name !== 'checked')
            .map((fieldItem) => {
              const field = flattenFields.find((item) => {
                return item.name === fieldItem.name;
              });

              return (
                <div className="uppercase text-gray-30" key={field.label}>
                  <div className="flex px-4 text-left">
                    {field.label}

                    {fieldItem.isSorting && <Sorting type={fieldItem.isSorting} handleSorting={handlers.sorting} />}
                  </div>
                </div>
              );
            })}
          <div className="uppercase text-gray-30">
            <div className="flex px-4 text-left">Actions</div>
          </div>
        </div>
        <div>
          {fieldArrayRenderProps.fields.map((name, index) => {
            return (
              <GridRowFlex
                key={`${name || fieldKey}-${index}`}
                setEditMode={setEdit}
                fields={filterVisibleFields(gridFields, flattenFields)}
                name={name}
                index={index}
                isChecked={isChecked}
                fractionGrid={fractionGrid}
              />
            );
          })}
        </div>
      </div>
    </>
  );
};

function filterVisibleFields(gridFields, flattenFields) {
  return gridFields.map((fieldName) =>
    flattenFields.find((item) => {
      return item.name === fieldName.name;
    })
  );
}

export default GridFlex;
